/**
 * Section: Live Shopping Hub
 * -----------------------------------------------------------------------------
 * - Section that renders Live Shopping metaobjects.
 *
 * @namespace LiveShoppingHub
 */
import './live-shopping-hub.scss'

import Bambuser from '~/api/bambuser'

import classes from '~/helpers/classes'
import { parseAttributeValue } from '~/helpers/dom'

/**
 * DOM selectors.
 */
const selectors = {
  card: '[js-shopping-card]',
  grid: '[js-shopping-grid]',
  loadMore: '[js-load-more]',
  paginatedCards: '[js-paginated-cards]',
  preloader: '[js-shopping-preloader]',
}

/**
 * Create a new Live Shopping Hub section object.
 * @param {HTMLElement} section - Scoped [data-mint-section] <section> element.
 */
export default ({ section = false } = false) => {

  /**
   * Node selectors.
   */
  let nodeSelectors = {}
  const state = {}

  /**
   * Set DOM Nodes based on selectors.
   */
  function setNodeSelectors() {
    if (!section) {
      return
    }

    nodeSelectors = {
      grid: section.querySelector(selectors.grid),
      loadMoreBtn: section.querySelector(selectors.loadMore),
      paginatedCards: [...section.querySelectorAll(selectors.paginatedCards)],
      preloader: section.querySelector(selectors.preloader),
    }
  }

  /**
   * Set default state variables.
   */
  function setDefaultState() {
    state.paginateBy = parseAttributeValue(section.getAttribute('data-paginate-by'))
    state.trackingParameters = parseAttributeValue(section.getAttribute('data-tracking-parameters'))
  }

  /**
   * Set Event Listeners.
   */
  function setEventListeners() {
    nodeSelectors.loadMoreBtn?.addEventListener('click', () => {
      handleLoadMore()
    })
  }

  /**
   * Custom load more pagination.
   */
  function handleLoadMore() {
    nodeSelectors.loadMoreBtn.classList.add(classes.hidden)
    nodeSelectors.preloader.classList.remove(classes.hidden)

    /**
     * Paginate items by slicing the first N items and then mutating
     * the original array. Repeat until no more items left.
     */
    const newItems = nodeSelectors.paginatedCards.slice(0, state.paginateBy)

    nodeSelectors.paginatedCards = nodeSelectors.paginatedCards.slice(state.paginateBy, nodeSelectors.paginatedCards.length)

    try {
      newItems.forEach((item) => {

        /**
         * Insert each new item before the "Load More" button wrapper
         */
        nodeSelectors.grid.insertAdjacentHTML('beforeend', item.innerHTML)
      })

    } catch (error) {
      mint.Log('error', 'Error during loading metaobjects', error)

    } finally {

      /**
       * On the last page, remove the button.
       */
      if (!nodeSelectors.paginatedCards.length) {
        nodeSelectors.loadMoreBtn.parentNode.remove()
      }

      nodeSelectors.loadMoreBtn?.classList.remove(classes.hidden)
      nodeSelectors.preloader?.classList.add(classes.hidden)
    }
  }

  /**
   * Initialise component.
   */
  function init() {
    setDefaultState()
    setNodeSelectors()
    setEventListeners()

    Bambuser.init()
    Bambuser.subscribeOnBambuserEvents({
      preventDrawer: true,
      trackingParameters: state.trackingParameters,
    })
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
